import * as React from 'react'

import { ChevronRightIcon, PlusIcon, SmartphoneIcon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { ICompany } from '~/client/graph'
import BaseAddEditDialogWrapper from '~/client/src/desktop/components/BaseAddEditDialogWrapper/BaseAddEditDialogWrapper'
import ConfirmDialogTypes from '~/client/src/desktop/enums/ConfirmDialogTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import FeatureFlag from '~/client/src/shared/components/FeatureFlag'
import QRCodeAccess from '~/client/src/shared/components/QRCodes/components/QRCodeAccess'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import FlaggedFeatures from '~/client/src/shared/enums/FlaggedFeatures'
import InfoSectionId, {
  getSectionTranslate,
} from '~/client/src/shared/enums/InfoSectionId'
import UserFieldId from '~/client/src/shared/enums/UserFieldId'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import { INVITE_PROJECT_MEMBERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import { IAddEditDialogField } from '~/client/src/shared/stores/ui/AddEditItemDialog.store'
import { IPhoneClaimConflict } from '~/client/src/shared/utils/bulkSaveErrorFormatter'
import { areObjectArraysEqual } from '~/client/src/shared/utils/util'

import CompanyDialog from '../../../CompaniesDirectory/CompanyDialog/CompanyDialog'
import AddEditMemberDialogStore from './AddEditMemberDialog.store'
import ConfirmUsersDialog from './ConfirmUsersDialog/ConfirmUsersDialog'
import PermissionsGrid from './PermissionsGrid/PermissionsGrid'
import PhoneReassignConfirm from './PhoneReassignConfirm'
import ResetPasswordRow from './ResetPasswordRow'

interface IProps {
  onInvite?: (item: User, cb: () => void) => void
  onManagePhone?: (userId: string) => void
  // Bumped after a phone transfer/remove to clear the number from this form.
  phoneClearToken?: number
  isOpen: boolean
  closeDialog: () => void
  loading: boolean
  onSubmit: (
    items: User[],
    lastUpdatedItemId?: string,
    reassignClaimedPhone?: boolean,
  ) => void
  itemsToEdit: User[]
  allItems: User[]

  errorMessage?: string
  resetErrorMessage?: () => void
  // Set when the last save failed because the entered phone is already claimed;
  // an admin can transfer it to this user. Cleared by resetErrorMessage.
  phoneClaimConflict?: IPhoneClaimConflict | null

  companiesStore?: CompaniesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  state?: DesktopInitialState
}

@inject('state', 'companiesStore', 'userProjectsStore', 'projectMembersStore')
@observer
export default class AddEditMemberDialog extends React.Component<IProps> {
  @observable private errorMessage: string = null
  @observable private onInvite: (item: User, cb: () => void) => void = null
  // Set for the next save only (consumed in handleSubmit) when an admin confirms
  // reassigning a claimed phone from the save-conflict popup.
  private reassignPhoneOnSave = false
  private readonly store: AddEditMemberDialogStore = null
  // Portal into #app so the Manage Phone modal and the global user card (both in
  // #app, which has `isolation: isolate`) can layer above this dialog.
  private readonly appPortalContainer: React.RefObject<HTMLElement | null> = {
    current: document.getElementById('app'),
  }

  public constructor(props: IProps) {
    super(props)

    this.errorMessage = props.errorMessage
    this.onInvite = props.onInvite
    this.store = new AddEditMemberDialogStore(
      props.itemsToEdit,
      props.allItems,
      props.resetErrorMessage,
      props.companiesStore,
      props.userProjectsStore,
      this.handleSubmit,
      props.closeDialog,
      props.state,
    )
    makeObservable(this)
  }

  // Wraps the parent's onSubmit so a one-shot "reassign the claimed phone"
  // flag (set by the save-conflict popup) rides along with the save. Reads
  // this.props.onSubmit dynamically, so it stays current without re-binding.
  private handleSubmit = (items: User[], lastUpdatedItemId?: string) => {
    const reassignClaimedPhone = this.reassignPhoneOnSave
    this.reassignPhoneOnSave = false
    this.props.onSubmit(items, lastUpdatedItemId, reassignClaimedPhone)
  }

  public componentDidMount() {
    this.store.recreateFormStores()
    this.store.updateFormStores()
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    runInAction(() => {
      if (this.errorMessage !== this.props.errorMessage)
        this.errorMessage = this.props.errorMessage
      if (this.onInvite !== this.props.onInvite)
        this.onInvite = this.props.onInvite

      this.store.allItems = this.props.allItems

      if (
        !areObjectArraysEqual(prevProps.itemsToEdit, this.props.itemsToEdit)
      ) {
        this.store.itemsToEdit = this.props.itemsToEdit
        this.store.recreateFormStores()
        this.store.updateFormStores()
      }

      if (prevProps.onSubmit !== this.props.onSubmit) {
        // handleSubmit reads this.props.onSubmit dynamically; keep the store
        // pointed at the wrapper (not the raw prop) so the reassign flag rides along.
        this.store.updateItems = this.handleSubmit
      }

      if (
        prevProps.phoneClearToken !== this.props.phoneClearToken &&
        !this.store.isAddMode &&
        this.store.editingItem?.id
      ) {
        this.store.activeFormStore.handleFieldValueReset(
          UserFieldId.PhoneNumber,
        )
      }
    })
  }

  public render() {
    const { itemsToEdit, isOpen, state, onInvite } = this.props
    const {
      allCompanies,
      hideCompanyModal,
      shouldShowCompanyModal,
      saveRelatedCompany: saveCompanies,
      areCompaniesBeingUpdated,
      relatedCompany,
      shouldShowConfirmInviteDialog,
      hideConfirmInviteDialog,
      editingItem,
    } = this.store

    if (!itemsToEdit?.length || !isOpen) {
      return <div />
    }

    return (
      <>
        <BaseAddEditDialogWrapper
          store={this.store}
          isLoading={this.props.loading}
          errorInfo={
            this.store.activeFormStore.error || this.props.errorMessage
          }
          customFooterRenderer={this.renderFooter}
          portalContainer={this.appPortalContainer}
          modal="trap-focus"
        >
          {onInvite && (
            <ConfirmUsersDialog
              type={ConfirmDialogTypes.INVITE}
              isOpen={shouldShowConfirmInviteDialog}
              loading={state.loading.get(INVITE_PROJECT_MEMBERS)}
              closeDialog={hideConfirmInviteDialog}
              performItems={this.handleInviteConfirmClick}
              itemsToPerform={[User.fromDto(editingItem)]}
            />
          )}
          <div className="row y-start">
            {this.renderSection(InfoSectionId.Personal)}
            <div className="mx24 my12 no-grow ba-none br-light-grey as-stretch" />
            {this.renderSection(InfoSectionId.Company)}
            {this.renderToCompanyIcon(relatedCompany)}
          </div>
          <div className="ma12 ba-none bb-light-grey as-stretch" />
          <div className="row y-start">
            {this.renderSection(InfoSectionId.Project)}
            <div className="mx24 my12 no-grow ba-none br-light-grey as-stretch" />
            {this.renderSection(InfoSectionId.Additional)}
          </div>
          {this.renderWorkflowPermissionSection()}
          {this.store.editingItem?.id && (
            <>
              <div className="ma12 ba-none bb-light-grey as-stretch" />
              {this.renderSection(InfoSectionId.QRCodes)}
            </>
          )}
          {shouldShowCompanyModal && (
            <CompanyDialog
              closeDialog={hideCompanyModal}
              loading={areCompaniesBeingUpdated}
              onSubmit={saveCompanies}
              itemsToEdit={[relatedCompany]}
              allItems={allCompanies}
            />
          )}
        </BaseAddEditDialogWrapper>
        {this.isAdmin && (
          <PhoneReassignConfirm
            conflict={this.props.phoneClaimConflict ?? null}
            targetName={this.editingItemName}
            onCancel={() => this.props.resetErrorMessage?.()}
            onConfirm={() => {
              // Re-save with the reassign flag; the backend moves the number
              // onto this user (create or edit) as part of the save.
              this.reassignPhoneOnSave = true
              this.props.resetErrorMessage?.()
              this.store.submit()
            }}
          />
        )}
      </>
    )
  }

  private get isAdmin(): boolean {
    const { state, userProjectsStore } = this.props
    return !!(
      state?.user &&
      userProjectsStore &&
      UserProject.isAdmin(state.user, userProjectsStore)
    )
  }

  private get editingItemName(): string {
    const item = this.store.editingItem
    const name = [item?.firstName, item?.lastName].filter(Boolean).join(' ')
    return name || item?.email || 'this user'
  }

  private renderWorkflowPermissionSection(): JSX.Element {
    return (
      <FeatureFlag
        features={[FlaggedFeatures.EDIT_USER_WORKFLOW_PERMISSIONS]}
        defaultValues={[false]}
        render={([isEnabled]) =>
          isEnabled ? (
            <>
              <div className="ma12 ba-none bb-light-grey as-stretch" />
              {this.renderSection(InfoSectionId.WorkflowPermission)}
            </>
          ) : null
        }
      />
    )
  }

  private renderSection(sectionId: InfoSectionId): JSX.Element {
    const {
      getFieldsBySectionId,
      getPermissionConfig,
      handlePermissionChange,
    } = this.store

    const fields = getFieldsBySectionId(sectionId)
    const title = getSectionTranslate(sectionId)

    let content

    switch (sectionId) {
      case InfoSectionId.WorkflowPermission:
        content = (
          <PermissionsGrid
            value={getPermissionConfig()}
            onChange={handlePermissionChange}
          />
        )
        break
      case InfoSectionId.QRCodes:
        content = (
          <QRCodeAccess
            user={this.store.editingItem}
            shouldShowQRCode={true}
            className="pl20"
          />
        )
        break
      case InfoSectionId.Personal:
        content = this.renderPersonalSectionContent(fields)
        break
      default:
        content = BaseAddEditDialogWrapper.renderFields(fields, AvatarType.User)
        break
    }

    return (
      <div>
        <div className="text bold uppercase light lp05 py12">{title}</div>
        {content}
      </div>
    )
  }

  private renderFooter = (): JSX.Element => {
    if (this.errorInfo) {
      return BaseAddEditDialogWrapper.renderErrorInfo(this.errorInfo)
    }

    if (this.onInvite) {
      const { isAddMode, editingItem, showConfirmInviteDialog } = this.store
      return (
        <ResetPasswordRow
          isAddMode={isAddMode}
          user={editingItem}
          onInvite={showConfirmInviteDialog}
        />
      )
    }
  }

  // Personal fields with the Manage Phone Number button inserted under the Phone input.
  private renderPersonalSectionContent(
    fields: IAddEditDialogField[],
  ): React.ReactNode {
    const manageButton = this.renderManagePhoneButton()
    if (!manageButton) {
      return BaseAddEditDialogWrapper.renderFields(fields, AvatarType.User)
    }
    const phoneIndex = fields.findIndex(f => f.id === UserFieldId.PhoneNumber)
    if (phoneIndex === -1) {
      return BaseAddEditDialogWrapper.renderFields(fields, AvatarType.User)
    }
    return (
      <>
        {BaseAddEditDialogWrapper.renderFields(
          fields.slice(0, phoneIndex + 1),
          AvatarType.User,
        )}
        {manageButton}
        {BaseAddEditDialogWrapper.renderFields(
          fields.slice(phoneIndex + 1),
          AvatarType.User,
        )}
      </>
    )
  }

  // Only for an existing user that has a number; opens the phone modal on top of
  // this dialog so unsaved edits are preserved.
  private renderManagePhoneButton = (): JSX.Element => {
    const { onManagePhone, projectMembersStore } = this.props
    const { isAddMode, editingItem } = this.store
    // Guard on the live store phone (updated in place after a transfer/remove),
    // so the button disappears once the user no longer has a number.
    const hasPhone = !!projectMembersStore?.getById(editingItem?.id)
      ?.phoneNumber
    // Managing a phone (transfer/unlink) is admin-only (also enforced by Graph).
    if (
      isAddMode ||
      !onManagePhone ||
      !editingItem?.id ||
      !hasPhone ||
      !this.isAdmin
    ) {
      return null
    }
    // Wrapped in px-2 (matching the form fields' px8) so the button lines up with
    // the Phone input while keeping its own padding.
    return (
      <div className="tw:-mt-2 tw:px-2">
        <Button
          variant="ghost"
          className="tw:text-primary tw:hover:text-primary"
          onClick={() => onManagePhone(editingItem.id)}
        >
          <SmartphoneIcon className="tw:size-4" />
          Manage Phone Number
        </Button>
      </div>
    )
  }

  public renderToCompanyIcon({ isDeleted, id }: ICompany): JSX.Element {
    if (isDeleted) {
      return null
    }

    const ToCompanyIcon = id ? ChevronRightIcon : PlusIcon

    return (
      <div className="row company-card-btn tw:max-w-0 tw:mt-20">
        <ToCompanyIcon
          size={16}
          className="to-company-icon pointer tw:text-primary"
          onClick={this.handleEditClick}
        />
      </div>
    )
  }

  private get errorInfo(): string {
    return this.store.activeFormStore.error || this.errorMessage
  }

  private handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    this.store.showCompanyModal()
  }

  private handleInviteConfirmClick = ([user]: User[]): void => {
    this.props.onInvite(user, this.store.hideConfirmInviteDialog)
  }
}
